import React from 'react';
import { getCurrentSportOptions, getSportDefaults } from '../../config/sportsConfig';

/**
 * SportMatchConfig — reusable component for configuring sport-specific
 * match rules (points_to_win, win_by_margin, max_points, total_games).
 *
 * Props:
 *   sportName   {string}  — sport name to look up options/defaults
 *   config      {object}  — { points_to_win, win_by_margin, max_points, total_games }
 *   onChange    {fn}      — called with (newConfig) when any select changes
 *   compact     {boolean} — if true, uses form-row-config layout (4-per-row)
 */
const SportMatchConfig = ({ sportName, config = {}, onChange, compact = false }) => {
  if (!sportName) {
    return (
      <div className="form-info-message">
        <span className="info-icon">ℹ️</span>
        <span>Select a sport to configure match rules</span>
      </div>
    );
  }

  const options = getCurrentSportOptions(sportName);
  const defaults = getSportDefaults(sportName);

  // Merge provided config with sport defaults for display
  const current = {
    points_to_win: config.points_to_win !== undefined ? config.points_to_win : defaults.points_to_win,
    win_by_margin: config.win_by_margin !== undefined ? config.win_by_margin : (defaults.win_by_margin || 1),
    max_points:    config.max_points    !== undefined ? config.max_points    : defaults.max_points,
    total_games:   config.total_games   !== undefined ? config.total_games   : (defaults.total_games || 1),
  };

  const handleChange = (key, rawValue) => {
    // 0 is a valid choice ("No Margin") — only empty / 'null' / non-numeric become null
    const parsed = parseInt(rawValue, 10);
    const value = rawValue === '' || rawValue === 'null' || Number.isNaN(parsed) ? null : parsed;
    onChange({ ...current, [key]: value });
  };

  const rowClass = compact ? 'form-row-config' : 'form-row-gaming';

  return (
    <div>
      <div className={rowClass}>
        <div className="form-group-gaming">
          <label className="form-label-gaming">Points to Win</label>
          <select
            value={current.points_to_win ?? ''}
            onChange={(e) => handleChange('points_to_win', e.target.value)}
            className="form-select-gaming"
          >
            {(options.points_to_win || []).map(opt => (
              <option key={opt.value ?? 'null'} value={opt.value ?? ''}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group-gaming">
          <label className="form-label-gaming">Win by Margin</label>
          <select
            value={current.win_by_margin ?? ''}
            onChange={(e) => handleChange('win_by_margin', e.target.value)}
            className="form-select-gaming"
          >
            {(options.win_by_margin || []).map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group-gaming">
          <label className="form-label-gaming">Max Points</label>
          <select
            value={current.max_points ?? ''}
            onChange={(e) => handleChange('max_points', e.target.value)}
            className="form-select-gaming"
          >
            {(options.max_points || []).map(opt => (
              <option key={opt.value ?? 'null'} value={opt.value ?? ''}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group-gaming">
          <label className="form-label-gaming">Match Format</label>
          <select
            value={current.total_games ?? ''}
            onChange={(e) => handleChange('total_games', e.target.value)}
            className="form-select-gaming"
          >
            {(options.total_games || []).map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="config-summary-inline">
        <span className="config-summary-icon">📋</span>
        Best of <strong>{current.total_games}</strong>
        {current.points_to_win ? ` · ${current.points_to_win} pts to win` : ''}
        {current.win_by_margin > 0 ? ` · win by ${current.win_by_margin}` : ''}
        {current.max_points && current.max_points < 999 ? ` · max ${current.max_points}` : ''}
      </div>
    </div>
  );
};

export default SportMatchConfig;
